import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { useScrollReveal } from "../../hooks/useScrollReveal";
import { RESUME_URL } from "../../constants";

/* ── Social icon paths (SVG d-paths, viewBox 0 0 24 24) ── */
const SOCIAL_ICONS: Record<string, string> = {
  GitHub: "M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z",
  LeetCode: "M16.102 17.93l-2.697 2.607c-.466.467-1.111.662-1.823.662s-1.357-.195-1.824-.662l-4.332-4.363c-.467-.467-.702-1.15-.702-1.863s.235-1.357.702-1.824l4.319-4.38c.467-.467 1.125-.645 1.837-.645s1.357.195 1.823.662l2.697 2.606c.514.515 1.365.497 1.9-.038.535-.536.553-1.387.038-1.901l-2.609-2.636a5.055 5.055 0 00-2.445-1.337l2.467-2.503c.516-.514.498-1.366-.037-1.901-.535-.535-1.387-.552-1.902-.038l-10.1 10.101c-.981.982-1.494 2.337-1.494 3.835 0 1.498.513 2.895 1.494 3.875l4.347 4.361c.981.979 2.337 1.452 3.834 1.452s2.853-.512 3.835-1.494l2.609-2.637c.514-.514.496-1.365-.039-1.9s-1.386-.553-1.899-.039zM20.811 13.01H10.666c-.702 0-1.27.604-1.27 1.346s.568 1.346 1.27 1.346h10.145c.701 0 1.27-.604 1.27-1.346s-.569-1.346-1.27-1.346z",
  LinkedIn: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z",
  Twitter: "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z",
  Blogs: "M19 2H6c-1.206 0-3 .799-3 3v14c0 2.201 1.794 3 3 3h15v-2H6.012C5.55 19.988 5 19.806 5 19s.55-.988 1.012-1H21V4c0-1.103-.897-2-2-2zm0 14H5V5c0-.806.55-.988 1-1h13v12z",
  Codolio: "M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5",
  Portfolio: "M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z M9 22V12h6v10",
};

const SOCIALS = [
  { label: "GitHub", url: "https://github.com/jugshaurya", hoverColor: "#8b5cf6" },
  { label: "LeetCode", url: "https://leetcode.com/u/jugshaurya/", hoverColor: "#ffa116" },
  { label: "LinkedIn", url: "https://www.linkedin.com/in/jugshaurya/", hoverColor: "#0a66c2" },
  { label: "Twitter", url: "https://twitter.com/jugshaurya", hoverColor: "#1da1f2" },
  { label: "Blogs", url: "https://notes.jugshaurya.vercel.app/", hoverColor: "#4ecdc4" },
  { label: "Codolio", url: "https://codolio.com/profile/jugshaurya", hoverColor: "#2ecc71" },
  { label: "Portfolio", url: "https://jugshaurya.vercel.app/", hoverColor: "#e74c3c" },
];

/* OWL Results removed from contact page */

/* ── 3D Owl SVG (detailed, shaded) ── */
const OwlSVG: React.FC<{ flip?: boolean; perched?: boolean; pupilX?: number; pupilY?: number; flapping?: boolean }> = ({ flip, perched, pupilX = 0, pupilY = 0, flapping }) => (
  <svg
    viewBox={perched ? "0 0 120 110" : "0 0 120 85"}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ transform: flip ? "scaleX(-1)" : undefined }}
  >
    <defs>
      <radialGradient id="owlBody" cx="50%" cy="40%" r="55%">
        <stop offset="0%" stopColor="#6b5038" />
        <stop offset="50%" stopColor="#4a3628" />
        <stop offset="100%" stopColor="#2a1a10" />
      </radialGradient>
      <radialGradient id="owlHead" cx="45%" cy="35%" r="60%">
        <stop offset="0%" stopColor="#7a6048" />
        <stop offset="50%" stopColor="#5a4232" />
        <stop offset="100%" stopColor="#3a2818" />
      </radialGradient>
      <radialGradient id="owlEye" cx="35%" cy="30%" r="50%">
        <stop offset="0%" stopColor="#ffe066" />
        <stop offset="60%" stopColor="#ffcc00" />
        <stop offset="100%" stopColor="#cc9900" />
      </radialGradient>
      <radialGradient id="owlWing" cx="60%" cy="30%" r="70%">
        <stop offset="0%" stopColor="#4a3628" />
        <stop offset="100%" stopColor="#1a0e06" />
      </radialGradient>
      <filter id="owlShadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#000000" floodOpacity="0.4" />
      </filter>
    </defs>

    <g filter="url(#owlShadow)">
      {/* Body — 3D shaded oval */}
      <ellipse cx="60" cy="52" rx="20" ry="24" fill="url(#owlBody)" />
      {/* Chest feather pattern */}
      <ellipse cx="60" cy="55" rx="13" ry="16" fill="none" stroke="#5a4430" strokeWidth="0.5" opacity="0.5" />
      <ellipse cx="60" cy="58" rx="10" ry="12" fill="none" stroke="#6a5440" strokeWidth="0.4" opacity="0.4" />
      <ellipse cx="60" cy="61" rx="7" ry="8" fill="none" stroke="#7a6450" strokeWidth="0.3" opacity="0.3" />
      {/* Chest highlight */}
      <ellipse cx="58" cy="50" rx="8" ry="10" fill="#6b5540" opacity="0.3" />

      {/* Head — 3D shaded */}
      <circle cx="60" cy="28" r="16" fill="url(#owlHead)" />
      {/* Facial disc (lighter ring around eyes) */}
      <ellipse cx="54" cy="27" rx="10" ry="11" fill="#5a4838" opacity="0.5" />
      <ellipse cx="66" cy="27" rx="10" ry="11" fill="#5a4838" opacity="0.5" />
      {/* Head highlight */}
      <circle cx="55" cy="22" r="6" fill="#7a6850" opacity="0.2" />

      {/* Ear tufts — feathered */}
      <path d="M48,16 L43,4 Q46,10 50,14 Z" fill="#5a4232" />
      <path d="M49,15 L45,6 Q47,11 51,14 Z" fill="#4a3222" />
      <path d="M72,16 L77,4 Q74,10 70,14 Z" fill="#5a4232" />
      <path d="M71,15 L75,6 Q73,11 69,14 Z" fill="#4a3222" />

      {/* Eyes — glowing 3D */}
      <circle cx="53" cy="26" r="6.5" fill="#1a1008" />
      <circle cx="67" cy="26" r="6.5" fill="#1a1008" />
      <circle cx="53" cy="26" r="5.5" fill="url(#owlEye)" />
      <circle cx="67" cy="26" r="5.5" fill="url(#owlEye)" />
      {/* Pupils — follow cursor for perched owl */}
      <circle cx={53 + pupilX} cy={26 + pupilY} r="3" fill="#0a0a00" />
      <circle cx={67 + pupilX} cy={26 + pupilY} r="3" fill="#0a0a00" />
      {/* Eye specular highlights */}
      <circle cx={51 + pupilX * 0.5} cy={24 + pupilY * 0.5} r="1.5" fill="white" opacity="0.7" />
      <circle cx={65 + pupilX * 0.5} cy={24 + pupilY * 0.5} r="1.5" fill="white" opacity="0.7" />
      <circle cx={54 + pupilX * 0.3} cy={28 + pupilY * 0.3} r="0.8" fill="white" opacity="0.3" />
      <circle cx={68 + pupilX * 0.3} cy={28 + pupilY * 0.3} r="0.8" fill="white" opacity="0.3" />

      {/* Beak — 3D with highlight */}
      <path d="M57,32 L60,38 L63,32 Q60,34 57,32 Z" fill="#b8860b" />
      <path d="M58,32 L60,36 L61,32 Q60,33 58,32 Z" fill="#daa520" opacity="0.5" />

      {/* Wings — 3D layered feathers */}
      {!perched && <>
        {/* Left wing */}
        <path d="M40,38 Q15,28 4,50 Q8,48 14,46 Q10,52 6,58 Q18,50 40,56 Z" fill="url(#owlWing)" />
        <path d="M38,40 Q20,34 10,50 Q18,46 35,52 Z" fill="#3a2818" opacity="0.6" />
        {/* Left wing feather lines */}
        <path d="M35,42 Q22,38 12,48" stroke="#2a1a0e" strokeWidth="0.5" fill="none" opacity="0.4" />
        <path d="M33,45 Q22,42 14,50" stroke="#2a1a0e" strokeWidth="0.4" fill="none" opacity="0.3" />
        {/* Right wing */}
        <path d="M80,38 Q105,28 116,50 Q112,48 106,46 Q110,52 114,58 Q102,50 80,56 Z" fill="url(#owlWing)" />
        <path d="M82,40 Q100,34 110,50 Q102,46 85,52 Z" fill="#3a2818" opacity="0.6" />
        <path d="M85,42 Q98,38 108,48" stroke="#2a1a0e" strokeWidth="0.5" fill="none" opacity="0.4" />
        <path d="M87,45 Q98,42 106,50" stroke="#2a1a0e" strokeWidth="0.4" fill="none" opacity="0.3" />
      </>}
      {/* Perched wings — folded or flapping */}
      {perched && !flapping && <>
        <path d="M42,40 Q38,50 40,68 Q42,62 44,56 Q44,64 43,70 Q48,58 46,44 Z" fill="url(#owlWing)" />
        <path d="M78,40 Q82,50 80,68 Q78,62 76,56 Q76,64 77,70 Q72,58 74,44 Z" fill="url(#owlWing)" />
        <path d="M43,44 Q40,54 41,64" stroke="#2a1a0e" strokeWidth="0.4" fill="none" opacity="0.3" />
        <path d="M77,44 Q80,54 79,64" stroke="#2a1a0e" strokeWidth="0.4" fill="none" opacity="0.3" />
      </>}
      {perched && flapping && <>
        {/* Left wing — spread out and flapping */}
        <g className="owl-wing-left">
          <path d="M40,38 Q15,28 4,50 Q8,48 14,46 Q10,52 6,58 Q18,50 40,56 Z" fill="url(#owlWing)" />
          <path d="M38,40 Q20,34 10,50 Q18,46 35,52 Z" fill="#3a2818" opacity="0.6" />
        </g>
        {/* Right wing — spread out and flapping */}
        <g className="owl-wing-right">
          <path d="M80,38 Q105,28 116,50 Q112,48 106,46 Q110,52 114,58 Q102,50 80,56 Z" fill="url(#owlWing)" />
          <path d="M82,40 Q100,34 110,50 Q102,46 85,52 Z" fill="#3a2818" opacity="0.6" />
        </g>
      </>}

      {/* Tail feathers */}
      <path d="M52,72 L50,78 L54,76 Z" fill="#3a2818" opacity="0.7" />
      <path d="M58,73 L58,80 L62,80 L62,73 Z" fill="#3a2818" opacity="0.6" />
      <path d="M68,72 L70,78 L66,76 Z" fill="#3a2818" opacity="0.7" />

      {/* Talons */}
      {perched ? <>
        {/* Perched talons gripping */}
        <path d="M50,74 L48,80 L46,84" stroke="#8b7520" strokeWidth="1.5" strokeLinecap="round" fill="none" />
        <path d="M50,74 L50,81 L49,85" stroke="#8b7520" strokeWidth="1.5" strokeLinecap="round" fill="none" />
        <path d="M50,74 L52,80 L53,84" stroke="#8b7520" strokeWidth="1.5" strokeLinecap="round" fill="none" />
        <path d="M70,74 L68,80 L66,84" stroke="#8b7520" strokeWidth="1.5" strokeLinecap="round" fill="none" />
        <path d="M70,74 L70,81 L69,85" stroke="#8b7520" strokeWidth="1.5" strokeLinecap="round" fill="none" />
        <path d="M70,74 L72,80 L73,84" stroke="#8b7520" strokeWidth="1.5" strokeLinecap="round" fill="none" />
      </> : <>
        {/* Flying talons holding letter */}
        <rect x="49" y="70" width="22" height="14" rx="2" fill="#f5e6c8" stroke="#c4942a" strokeWidth="0.8" />
        <path d="M49,77 L60,70 L71,77" stroke="#8b0000" strokeWidth="0.8" fill="none" />
        <circle cx="60" cy="77" r="3.5" fill="#8b0000" />
        <text x="60" y="79.5" textAnchor="middle" fontSize="4.5" fill="#ffd700" fontWeight="bold">H</text>
        {/* Talon grips on letter */}
        <path d="M52,70 L50,68 L52,69" stroke="#8b7520" strokeWidth="1" fill="none" />
        <path d="M68,70 L70,68 L68,69" stroke="#8b7520" strokeWidth="1" fill="none" />
      </>}
    </g>
  </svg>
);

/* ── Floating letter SVG ── */
const LetterSVG: React.FC = () => (
  <svg viewBox="0 0 40 30" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="2" width="36" height="26" rx="2" fill="#f5e6c8" stroke="#c4942a" strokeWidth="1" />
    <path d="M2,2 L20,16 L38,2" stroke="#8b0000" strokeWidth="0.8" fill="none" />
    <circle cx="20" cy="20" r="4" fill="#8b0000" />
    <text x="20" y="22" textAnchor="middle" fontSize="5" fill="#ffd700" fontWeight="bold">H</text>
  </svg>
);

/* ── Main component ── */
const Contact: React.FC = () => {
  const { ref, isVisible } = useScrollReveal(0.15);
  const [owlsVisible, setOwlsVisible] = useState(false);
  const [pupil, setPupil] = useState({ x: 0, y: 0 });
  const [owlFlapping, setOwlFlapping] = useState(false);
  const flapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const letterRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => setOwlsVisible(true), 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  /* Trigger owl wing flap */
  const triggerFlap = useCallback(() => {
    setOwlFlapping(true);
    if (flapTimerRef.current) clearTimeout(flapTimerRef.current);
    flapTimerRef.current = setTimeout(() => setOwlFlapping(false), 2000);
  }, []);

  /* Track cursor relative to perched owl for eye movement */
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!letterRef.current) return;
    const rect = letterRef.current.getBoundingClientRect();
    const owlCenterX = rect.right - 60;
    const owlCenterY = rect.top - 35;
    const dx = e.clientX - owlCenterX;
    const dy = e.clientY - owlCenterY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const maxOffset = 2.2;
    const scale = Math.min(maxOffset / (dist * 0.01 + 1), maxOffset);
    setPupil({
      x: (dx / (dist || 1)) * scale,
      y: (dy / (dist || 1)) * scale,
    });
  }, []);

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [handleMouseMove]);

  /* Memoize random positions so they don't change on re-render */
  const stars = useMemo(
    () => Array.from({ length: 40 }, () => ({
      x: Math.random() * 100,
      y: Math.random() * 60,
      size: Math.random() * 2 + 1,
      delay: Math.random() * 3,
    })),
    []
  );

  const letters = useMemo(
    () => Array.from({ length: 20 }, (_, i) => {
      // Push letters to left/right edges, avoid center
      const side = Math.random() > 0.5;
      const x = side ? 60 + Math.random() * 30 : 10 + Math.random() * 20;
      return {
        delay: i < 8 ? -(Math.random() * 8 + 2) : i * 0.8 + Math.random() * 1.5,
        x,
        duration: 7 + Math.random() * 5,
        size: 30 + Math.random() * 30,
        drift: Math.random() * 50 - 25,
      };
    }),
    []
  );

  return (
    <Section id="contact" ref={sectionRef}>
      {/* Night sky background */}
      <NightSky>
        {/* Moon */}
        <Moon />
        {/* Stars */}
        {stars.map((s, i) => (
          <Star
            key={i}
            $x={s.x}
            $y={s.y}
            $size={s.size}
            $delay={s.delay}
          />
        ))}
        {/* Hogwarts silhouette */}
        <HogwartsSilhouette />
      </NightSky>

      {/* Flying owls */}
      {owlsVisible && (
        <>
          <FlyingOwl $delay={0} $startX={-15} $endX={115} $y={10} $duration={14} $size={70}>
            <OwlSVG />
          </FlyingOwl>
          <FlyingOwl $delay={6} $startX={115} $endX={-15} $y={22} $duration={16} $size={55}>
            <OwlSVG flip />
          </FlyingOwl>
        </>
      )}

      {/* Floating letters */}
      {owlsVisible && (
        <>
          {letters.map((l, i) => (
            <FloatingLetter
              key={i}
              $delay={l.delay}
              $x={l.x}
              $duration={l.duration}
              $size={l.size}
              $drift={l.drift}
            >
              <LetterSVG />
            </FloatingLetter>
          ))}
        </>
      )}

      {/* Content */}
      <SectionInner ref={ref} $visible={isVisible}>
        <ThemeLabel>
          <WaxSeal>H</WaxSeal> Owl Post Delivery
        </ThemeLabel>

        <SectionTag>--- HOGWARTS SCHOOL of WITCHCRAFT and WIZARDRY ---</SectionTag>

        <SectionTitle>
          <TitleMain>Looking to Work with Shaurya?</TitleMain>
          <SectionTitleSub>Send an Owl. I'll Reply Before It Lands.</SectionTitleSub>
        </SectionTitle>

        <HogwartsLetter ref={letterRef}>
          <ParchmentLines />
          {/* Owl perched on the letter — eyes follow cursor */}
          <PerchedOwlWrapper $flapping={owlFlapping}>
            <OwlSVG perched pupilX={pupil.x} pupilY={pupil.y} flapping={owlFlapping} />
            <OwlHint>Move your cursor around me</OwlHint>
          </PerchedOwlWrapper>
          <LetterHeader>
            <LetterCrest>⚡</LetterCrest>
            <LetterAddress>
              Dear Hiring Manager / Recruiter,<br />
              <small>From the Desk of Shaurya Singhal</small>
            </LetterAddress>
          </LetterHeader>
          <LetterBody>
            We are pleased to inform you that <strong>Shaurya Singhal</strong> —
            Member of Technical Staff 2 at Adobe, LeetCode Guardian, and
            open-source contributor — is available for his next quest.
            <br /><br />
            <em>Owl back if you wish to recruit this wizard.</em>
          </LetterBody>
          <LetterSignature>
            Yours sincerely,<br />
            <strong>Shaurya Singhal</strong><br />
            <small>Member of Technical Staff 2 @ Adobe · Competitive Programmer · Open Source Contributor</small>
          </LetterSignature>
          <LetterCTA href="mailto:shauryasinghal84@gmail.com" onMouseEnter={triggerFlap} onClick={triggerFlap}>
            <LetterCTAIcon>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f5e6c8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </LetterCTAIcon>
            Send Owl Post
          </LetterCTA>
          <WaxSealDecor>H</WaxSealDecor>
        </HogwartsLetter>

        {/* ── Below-letter content ── */}
        <BelowFoldSection>

        {/* ── Portkeys (card-style social links) ── */}
        <PortkeySection>
          <PortkeyGrid>
            {SOCIALS.map((s) => (
              <PortkeyCard
                key={s.label}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                $hoverColor={s.hoverColor}
              >
                <PortkeyCardIcon>
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="#a0a0a0">
                    {s.label === "Codolio" || s.label === "Portfolio" ? (
                      <path d={SOCIAL_ICONS[s.label]} fill="none" stroke="#a0a0a0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    ) : (
                      <path d={SOCIAL_ICONS[s.label]} />
                    )}
                  </svg>
                </PortkeyCardIcon>
                <PortkeyCardLabel>{s.label}</PortkeyCardLabel>
              </PortkeyCard>
            ))}
          </PortkeyGrid>
        </PortkeySection>

        <CTARow>
          <CTAButton
            href="mailto:shauryasinghal84@gmail.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            ⚡ Send an Owl
          </CTAButton>
          <CTAButtonSecondary
            href="https://notes.jugshaurya.vercel.app/"
            target="_blank"
            rel="noopener noreferrer"
          >
            📜 Read the Chronicles
          </CTAButtonSecondary>
        </CTARow>

        <EmailText>shauryasinghal84@gmail.com</EmailText>

        {/* ── Wizard References ── */}
        <RefsTitle>Wizard References</RefsTitle>
        <RefsGrid>
          <RefCard>
            <RefDecorTop />
            <RefQuoteMark>"</RefQuoteMark>
            <RefText>
              Shaurya is a highly skilled engineer with a rare combination of deep technical expertise and collaborative spirit. His work on Naukri Campus — from performance optimization to building scalable recruiter tools — consistently exceeded expectations. He has an impressive ability to own complex features end-to-end.
            </RefText>
            <RefFooter>
              <RefAvatar>👨‍💻</RefAvatar>
              <RefInfo>
                <RefName>Engineering Lead</RefName>
                <RefRole>Infoedge (Naukri Campus) · 2023–2026</RefRole>
              </RefInfo>
              <RefHouse $color="#4169e1">Ravenclaw</RefHouse>
            </RefFooter>
            <RefDecorBottom />
          </RefCard>

          <RefCard>
            <RefDecorTop />
            <RefQuoteMark>"</RefQuoteMark>
            <RefText>
              Working with Shaurya during BOSS'20 was a great experience. He quickly understood our codebase, contributed 12 quality PRs, and built the admin impersonation feature with 90% test coverage. He's someone who can navigate any codebase and deliver solid results.
            </RefText>
            <RefFooter>
              <RefAvatar>🧙‍♂️</RefAvatar>
              <RefInfo>
                <RefName>Open Source Mentor</RefName>
                <RefRole>Coding Blocks</RefRole>
              </RefInfo>
              <RefHouse $color="#d4534a">Gryffindor</RefHouse>
            </RefFooter>
            <RefDecorBottom />
          </RefCard>
        </RefsGrid>

        {/* ── Nimbus 2000 Broomstick + Accio Resume ── */}
        <BroomSection>
          <BroomContainer>
            {/* Speed lines behind broom */}
            <SpeedLines>
              {Array.from({ length: 6 }).map((_, i) => (
                <SpeedLine key={i} $y={20 + i * 12} $delay={i * 0.15} $width={40 + Math.random() * 60} />
              ))}
            </SpeedLines>
            {/* Broom glow */}
            <BroomGlow />
            {/* Nimbus 2000 SVG */}
            <BroomSVG viewBox="0 0 500 140" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Bristle glow */}
              <ellipse cx={45} cy={70} rx={40} ry={35} fill="url(#bristleGlow)" opacity={0.5} />

              {/* Outer bristles — wide fan with curves */}
              {Array.from({ length: 24 }).map((_, i) => {
                const spread = (i - 12) * 3.2;
                const endY = 70 + spread;
                const curve = Math.abs(spread) * 0.3;
                const len = 55 + Math.abs(spread) * 0.8;
                return (
                  <path
                    key={`bo${i}`}
                    d={`M75,${68 + spread * 0.15} Q${50 - curve},${endY * 0.6 + 28} ${75 - len},${endY}`}
                    stroke={i % 4 === 0 ? "#daa520" : i % 3 === 0 ? "#c4942a" : "#a67c20"}
                    strokeWidth={i % 5 === 0 ? 2 : 1.2}
                    fill="none"
                    opacity={0.5 + (1 - Math.abs(spread) / 40) * 0.5}
                    strokeLinecap="round"
                  />
                );
              })}

              {/* Inner bristle cluster — denser core */}
              {Array.from({ length: 14 }).map((_, i) => {
                const spread = (i - 7) * 2;
                const endY = 70 + spread;
                return (
                  <path
                    key={`bi${i}`}
                    d={`M75,${68 + spread * 0.1} Q55,${endY * 0.7 + 21} ${35},${endY}`}
                    stroke={i % 2 === 0 ? "#c4942a" : "#b8860b"}
                    strokeWidth={1.8}
                    fill="none"
                    opacity={0.7}
                    strokeLinecap="round"
                  />
                );
              })}

              {/* Bristle base collar */}
              <ellipse cx={78} cy={70} rx={8} ry={20} fill="#6b4e12" />
              <ellipse cx={78} cy={70} rx={6} ry={18} fill="#8b6914" />

              {/* Binding rings (3 dark leather wraps) */}
              <rect x={86} y={55} width={10} height={30} rx={3} fill="#2a1a0a" />
              <rect x={87} y={57} width={8} height={26} rx={2} fill="#3a2510" />
              {/* ring grooves */}
              <line x1={87} y1={62} x2={95} y2={62} stroke="#1a0f05" strokeWidth={1} />
              <line x1={87} y1={70} x2={95} y2={70} stroke="#1a0f05" strokeWidth={1} />
              <line x1={87} y1={78} x2={95} y2={78} stroke="#1a0f05" strokeWidth={1} />

              {/* Second binding ring */}
              <rect x={100} y={57} width={7} height={26} rx={2} fill="#2a1a0a" />
              <rect x={101} y={59} width={5} height={22} rx={1.5} fill="#3a2510" />
              <line x1={101} y1={65} x2={106} y2={65} stroke="#1a0f05" strokeWidth={0.8} />
              <line x1={101} y1={75} x2={106} y2={75} stroke="#1a0f05" strokeWidth={0.8} />

              {/* Main handle shaft — thick with taper */}
              <path
                d="M108,70 Q200,66 320,62 Q400,58 470,52"
                stroke="url(#handleGrad)"
                strokeWidth={12}
                strokeLinecap="round"
                fill="none"
              />
              {/* Handle highlight (top edge shine) */}
              <path
                d="M110,65 Q200,62 320,58 Q400,54 468,49"
                stroke="rgba(255,220,120,0.15)"
                strokeWidth={3}
                fill="none"
                strokeLinecap="round"
              />
              {/* Handle shadow (bottom edge) */}
              <path
                d="M110,75 Q200,71 320,67 Q400,63 468,56"
                stroke="rgba(60,30,0,0.25)"
                strokeWidth={2}
                fill="none"
                strokeLinecap="round"
              />
              {/* Wood grain lines */}
              <path d="M130,67 Q260,63 400,57" stroke="rgba(139,105,20,0.2)" strokeWidth={0.6} fill="none" />
              <path d="M140,71 Q270,67 410,61" stroke="rgba(80,50,10,0.15)" strokeWidth={0.5} fill="none" />
              <path d="M150,69 Q280,65 420,59" stroke="rgba(160,120,30,0.12)" strokeWidth={0.4} fill="none" />

              {/* "Nimbus 2000" engraved text */}
              <text x={230} y={58} fontFamily="Cinzel, serif" fontSize={8} fill="#daa520" opacity={0.5} letterSpacing="3" fontStyle="italic">
                Nimbus 2000
              </text>

              {/* Handle tip — polished end cap */}
              <ellipse cx={472} cy={52} rx={7} ry={4} fill="#a67c20" />
              <ellipse cx={472} cy={51} rx={5} ry={2.5} fill="#c4942a" />

              {/* Gradient definitions */}
              <defs>
                <linearGradient id="handleGrad" x1="108" y1="70" x2="470" y2="52">
                  <stop offset="0%" stopColor="#8b6914" />
                  <stop offset="20%" stopColor="#a67c20" />
                  <stop offset="40%" stopColor="#c4942a" />
                  <stop offset="65%" stopColor="#daa520" />
                  <stop offset="85%" stopColor="#c4942a" />
                  <stop offset="100%" stopColor="#8b6914" />
                </linearGradient>
                <radialGradient id="bristleGlow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#ffd700" stopOpacity="0.3" />
                  <stop offset="60%" stopColor="#c4942a" stopOpacity="0.1" />
                  <stop offset="100%" stopColor="transparent" stopOpacity="0" />
                </radialGradient>
              </defs>
            </BroomSVG>
            {/* Sparkles around the broom */}
            {Array.from({ length: 8 }).map((_, i) => (
              <BroomSparkle
                key={i}
                $x={20 + Math.random() * 60}
                $y={20 + Math.random() * 60}
                $delay={i * 0.4}
                $size={2 + Math.random() * 3}
              />
            ))}
          </BroomContainer>

          <AccioResumeBtn
            href={RESUME_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <AccioBtnIcon>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3a2a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
            </AccioBtnIcon>
            <AccioBtnText>
              <AccioBtnTitle>Accio Resume</AccioBtnTitle>
              <AccioBtnSub>Click to summon the scroll</AccioBtnSub>
            </AccioBtnText>
            <AccioBtnArrow>→</AccioBtnArrow>
          </AccioResumeBtn>
        </BroomSection>

        <FooterGlow />
        <HogwartsQuote>
          "Happiness can be found, even in the darkest of times, if one only
          remembers to turn on the light."
          <QuoteAuthor>— Albus Dumbledore</QuoteAuthor>
        </HogwartsQuote>

        </BelowFoldSection>
      </SectionInner>
    </Section>
  );
};

export default Contact;

/* ══════════════════════════════════════════════════
   Animations
   ══════════════════════════════════════════════════ */

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const twinkle = keyframes`
  0%, 100% { opacity: 0.3; }
  50% { opacity: 1; }
`;

const moonGlow = keyframes`
  0%, 100% { box-shadow: 0 0 40px rgba(255, 248, 220, 0.3), 0 0 80px rgba(255, 248, 220, 0.1); }
  50% { box-shadow: 0 0 60px rgba(255, 248, 220, 0.5), 0 0 120px rgba(255, 248, 220, 0.15); }
`;

const owlFlapWing = keyframes`
  0%, 100% { transform: translateY(0px); }
  25% { transform: translateY(-8px); }
  50% { transform: translateY(3px); }
  75% { transform: translateY(-5px); }
`;

const sealPulse = keyframes`
  0%, 100% { box-shadow: 0 0 8px rgba(139, 0, 0, 0.4); }
  50% { box-shadow: 0 0 16px rgba(139, 0, 0, 0.7), 0 0 30px rgba(255, 215, 0, 0.2); }
`;

/* ══════════════════════════════════════════════════
   Styled Components
   ══════════════════════════════════════════════════ */

const Section = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 7rem 2rem;
  overflow: hidden;
  background: linear-gradient(
    180deg,
    #0a0a14 0%,
    #0a0e1a 20%,
    #0c1020 45%,
    #0e1225 65%,
    #12162a 80%,
    #161a30 90%,
    #1a1e36 100%
  );
  overflow-x: hidden;
`;

const NightSky = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
`;

const Moon = styled.div`
  position: absolute;
  top: 8%;
  right: 12%;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: radial-gradient(
    circle at 35% 35%,
    #fff8dc,
    #f0e68c 40%,
    #daa520 80%
  );
  animation: ${moonGlow} 4s ease-in-out infinite;

  /* craters */
  &::before {
    content: "";
    position: absolute;
    top: 20%;
    left: 30%;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: rgba(180, 160, 100, 0.3);
  }
  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 55%;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgba(180, 160, 100, 0.25);
  }

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    top: 5%;
    right: 8%;
  }
`;

const Star = styled.div<{ $x: number; $y: number; $size: number; $delay: number }>`
  position: absolute;
  left: ${({ $x }) => $x}%;
  top: ${({ $y }) => $y}%;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: #fff8dc;
  animation: ${twinkle} ${({ $delay }) => 2 + $delay}s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay}s;
`;

const HogwartsSilhouette = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 120px;
  background: linear-gradient(transparent 0%, #0a0a14 100%);

  &::before {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 80px;
    background:
      /* towers */
      linear-gradient(to right, transparent 5%, #0c1020 5%, #0c1020 7%, transparent 7%) 0 0 / 100% 100%,
      linear-gradient(to right, transparent 15%, #0e1225 15%, #0e1225 16%, transparent 16%) 0 -20px / 100% 100%,
      linear-gradient(to right, transparent 25%, #0c1020 25%, #0c1020 26%, transparent 26%) 0 -10px / 100% 100%,
      linear-gradient(to right, transparent 40%, #0e1225 40%, #0e1225 42%, transparent 42%) 0 -30px / 100% 100%,
      linear-gradient(to right, transparent 55%, #0c1020 55%, #0c1020 57%, transparent 57%) 0 -15px / 100% 100%,
      linear-gradient(to right, transparent 70%, #0e1225 70%, #0e1225 71%, transparent 71%) 0 -25px / 100% 100%,
      linear-gradient(to right, transparent 85%, #0c1020 85%, #0c1020 87%, transparent 87%) 0 -5px / 100% 100%,
      linear-gradient(to right, transparent 93%, #0e1225 93%, #0e1225 95%, transparent 95%) 0 -18px / 100% 100%;
  }
`;

/* ── Flying owl ── */
const FlyingOwl = styled.div<{
  $delay: number;
  $startX: number;
  $endX: number;
  $y: number;
  $duration: number;
  $size: number;
}>`
  position: absolute;
  top: ${({ $y }) => $y}%;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size * 0.67}px;
  pointer-events: none;
  z-index: 2;
  opacity: 0;

  animation:
    ${({ $startX, $endX }) => keyframes`
      0% { left: ${$startX}%; opacity: 0; }
      5% { opacity: 0.9; }
      95% { opacity: 0.9; }
      100% { left: ${$endX}%; opacity: 0; }
    `} ${({ $duration }) => $duration}s linear ${({ $delay }) => $delay}s infinite;

  svg {
    width: 100%;
    height: 100%;
    animation: ${owlFlapWing} 1.2s ease-in-out infinite;
    filter: drop-shadow(0 0 10px rgba(255, 215, 0, 0.15));
  }
`;

/* ── Floating letter ── */
const FloatingLetter = styled.div<{
  $delay: number;
  $x: number;
  $duration: number;
  $size: number;
  $drift: number;
}>`
  position: absolute;
  left: ${({ $x }) => $x}%;
  width: ${({ $size }) => $size}px;
  pointer-events: none;
  z-index: 1;
  opacity: 0;

  animation: ${({ $drift }) => keyframes`
    0% {
      opacity: 0;
      transform: translateY(100vh) translateX(0px) rotate(0deg);
    }
    10% { opacity: 0.8; }
    50% { transform: translateY(30vh) translateX(${$drift}px) rotate(360deg); }
    90% { opacity: 0.8; }
    100% {
      opacity: 0;
      transform: translateY(-50vh) translateX(${-$drift}px) rotate(720deg);
    }
  `} ${({ $duration }) => $duration}s ease-in-out ${({ $delay }) => $delay}s infinite both;

  svg {
    width: 100%;
    filter: drop-shadow(0 2px 8px rgba(196, 148, 42, 0.3));
  }
`;

/* ── Content ── */
const SectionInner = styled.div<{ $visible: boolean }>`
  position: relative;
  z-index: 5;
  max-width: 750px;
  width: 100%;
  text-align: center;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  animation: ${({ $visible }) => ($visible ? fadeUp : "none")} 0.7s ease both;
`;

const ThemeLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  font-family: "Cinzel", serif;
  font-size: 0.65rem;
  color: #c4942a;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  margin-bottom: 1.5rem;
`;

const WaxSeal = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: radial-gradient(circle, #8b0000, #5a0000);
  color: #ffd700;
  font-size: 0.6rem;
  font-weight: bold;
  font-family: "Cinzel", serif;
  animation: ${sealPulse} 2s ease-in-out infinite;
`;

const SectionTag = styled.p`
  font-family: "Cinzel", serif;
  font-size: 0.68rem;
  color: #8b6914;
  letter-spacing: 0.12em;
  margin-bottom: 0.5rem;
`;

/* ── Animated title ── */

const titleReveal = keyframes`
  0% { opacity: 0; transform: translateY(20px); filter: blur(8px); }
  100% { opacity: 1; transform: translateY(0); filter: blur(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const glowPulse = keyframes`
  0%, 100% { text-shadow: 0 0 20px rgba(184, 200, 224, 0.1), 0 0 40px rgba(184, 200, 224, 0.05); }
  50% { text-shadow: 0 0 30px rgba(184, 200, 224, 0.2), 0 0 60px rgba(184, 200, 224, 0.08), 0 2px 4px rgba(0,0,0,0.3); }
`;

const underlineGrow = keyframes`
  0% { width: 0; opacity: 0; }
  100% { width: 60%; opacity: 1; }
`;

const SectionTitle = styled.h2`
  margin-bottom: 2rem;
  position: relative;
  animation: ${titleReveal} 1s ease both;
  animation-delay: 0.2s;

  /* Decorative underline */
  &::after {
    content: "";
    display: block;
    height: 1px;
    margin: 1rem auto 0;
    background: linear-gradient(90deg, transparent, rgba(196, 148, 42, 0.4), rgba(184, 200, 224, 0.2), rgba(196, 148, 42, 0.4), transparent);
    animation: ${underlineGrow} 1.2s ease 0.8s both;
  }
`;

const TitleMain = styled.span`
  display: block;
  font-family: "Cinzel", serif;
  font-size: clamp(1.6rem, 4vw, 2.8rem);
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.02em;

  /* Shimmer gradient text */
  background: linear-gradient(
    90deg,
    #8a9bba 0%,
    #d4dce8 20%,
    #ffd700 40%,
    #d4dce8 60%,
    #8a9bba 80%,
    #d4dce8 100%
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation:
    ${shimmer} 6s linear infinite,
    ${glowPulse} 3s ease-in-out infinite;
`;

const SectionTitleSub = styled.span`
  display: block;
  font-family: "Crimson Text", serif;
  font-size: clamp(0.9rem, 1.8vw, 1.1rem);
  font-weight: 400;
  font-style: italic;
  color: #c4942a;
  margin-top: 0.6rem;
  opacity: 0;
  animation: ${titleReveal} 0.8s ease 0.6s both;
`;

/* ── Hogwarts Letter with zigzag edges ── */
const HogwartsLetter = styled.div`
  background: linear-gradient(
    145deg,
    #f5e6c8 0%,
    #ecdbb0 30%,
    #f0dfc0 60%,
    #e8d4a8 100%
  );
  border-left: 1px solid #c4942a;
  border-right: 1px solid #c4942a;
  padding: 2.8rem 2.8rem 3.8rem;
  margin-bottom: 2.5rem;
  margin-top: 10px;
  text-align: left;
  position: relative;
  box-shadow:
    0 4px 20px rgba(0, 0, 0, 0.4),
    inset 0 0 30px rgba(196, 148, 42, 0.1);

  /* Zigzag top edge — teeth pointing down into the letter */
  &::before {
    content: "";
    position: absolute;
    top: -10px;
    left: -1px;
    right: -1px;
    height: 10px;
    background:
      linear-gradient(315deg, #f5e6c8 33.33%, transparent 33.33%) 0 0 / 12px 10px,
      linear-gradient(45deg, #f5e6c8 33.33%, transparent 33.33%) 0 0 / 12px 10px;
    background-position: bottom left;
    background-repeat: repeat-x;
    filter: drop-shadow(0 -1px 0 #c4942a);
  }

  /* Zigzag bottom edge — teeth pointing up into the letter */
  &::after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: -1px;
    right: -1px;
    height: 10px;
    background:
      linear-gradient(135deg, #e8d4a8 33.33%, transparent 33.33%) 0 0 / 12px 10px,
      linear-gradient(225deg, #e8d4a8 33.33%, transparent 33.33%) 0 0 / 12px 10px;
    background-position: top left;
    background-repeat: repeat-x;
    filter: drop-shadow(0 1px 0 #c4942a);
  }
`;

/* Wax seal — separate element since ::after is used for zigzag */
const WaxSealDecor = styled.div`
  position: absolute;
  bottom: -20px;
  right: 24px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: radial-gradient(circle at 40% 35%, #a00000, #5a0000);
  color: #ffd700;
  font-family: "Cinzel", serif;
  font-size: 0.75rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(139, 0, 0, 0.6), inset 0 -1px 3px rgba(0,0,0,0.3);
  animation: ${sealPulse} 2s ease-in-out infinite;
  z-index: 3;
`;

const ParchmentLines = styled.div`
  position: absolute;
  inset: 0;
  background:
    repeating-linear-gradient(
      0deg,
      transparent,
      transparent 28px,
      rgba(139, 105, 20, 0.04) 28px,
      rgba(139, 105, 20, 0.04) 29px
    );
  pointer-events: none;
  z-index: 0;
`;

const LetterHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(139, 105, 20, 0.2);
`;

const LetterCrest = styled.div`
  font-size: 1.8rem;
  line-height: 1;
`;

const LetterAddress = styled.div`
  font-family: "Crimson Text", serif;
  font-size: 0.85rem;
  color: #3a2a1a;
  line-height: 1.6;

  small {
    font-size: 0.75rem;
    color: #6a5a3a;
  }
`;

const LetterBody = styled.div`
  font-family: "Crimson Text", serif;
  font-size: 1rem;
  color: #3a2a1a;
  line-height: 1.85;
  margin-bottom: 1.2rem;

  strong {
    color: #1a0a00;
  }

  em {
    color: #8b6914;
  }
`;

const LetterSignature = styled.div`
  font-family: "Crimson Text", serif;
  font-size: 0.92rem;
  color: #4a3a2a;
  line-height: 1.6;

  strong {
    font-family: "Cinzel", serif;
    font-size: 0.88rem;
    color: #2a1a0a;
  }

  small {
    font-size: 0.75rem;
    color: #8b6914;
    font-style: italic;
  }
`;

/* ── Buttons ── */
const CTARow = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
`;

const CTAButton = styled.a`
  font-family: "Cinzel", serif;
  font-size: 0.88rem;
  letter-spacing: 0.06em;
  padding: 0.85rem 2rem;
  background: rgba(139, 0, 0, 0.15);
  color: #ffd700;
  border: 1px solid rgba(255, 215, 0, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-block;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(45deg, transparent, rgba(255, 215, 0, 0.05), transparent);
    transform: translateX(-100%);
    transition: transform 0.5s;
  }

  &:hover {
    background: rgba(139, 0, 0, 0.25);
    box-shadow: 0 0 25px rgba(255, 215, 0, 0.15), 0 0 50px rgba(139, 0, 0, 0.1);
    transform: translateY(-3px);
    color: #ffd700;

    &::before {
      transform: translateX(100%);
    }
  }
`;

const CTAButtonSecondary = styled.a`
  font-family: "Cinzel", serif;
  font-size: 0.88rem;
  letter-spacing: 0.06em;
  padding: 0.85rem 2rem;
  background: transparent;
  color: #c4942a;
  border: 1px solid rgba(196, 148, 42, 0.2);
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-block;

  &:hover {
    color: #ffd700;
    border-color: rgba(255, 215, 0, 0.4);
    transform: translateY(-3px);
    box-shadow: 0 0 15px rgba(255, 215, 0, 0.08);
  }
`;

const EmailText = styled.p`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.72rem;
  color: #8b6914;
  margin-bottom: 2rem;
  opacity: 0.7;
`;

/* ── Below fold — everything after 100vh ── */
const BelowFoldSection = styled.div`
  width: 100%;
  padding-top: 3rem;
`;

const HighlightsCard = styled.div`
  background: linear-gradient(145deg, rgba(20, 15, 30, 0.9), rgba(15, 12, 22, 0.95));
  border: 1px solid rgba(255, 215, 0, 0.1);
  border-radius: 8px;
  padding: 1.5rem 1.5rem 1.2rem;
  margin-top: 1.5rem;
  margin-bottom: 2.5rem;
  text-align: left;
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;

  /* Parchment-like corner fold */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    width: 24px;
    height: 24px;
    background: linear-gradient(225deg, rgba(255, 215, 0, 0.06) 50%, transparent 50%);
  }
`;

const HighlightsTitle = styled.h3`
  font-family: "Cinzel", serif;
  font-size: 0.72rem;
  color: #ffd700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  margin-bottom: 1rem;
  padding-bottom: 0.6rem;
  border-bottom: 1px solid rgba(255, 215, 0, 0.08);
  text-align: center;
  text-shadow: 0 0 12px rgba(255, 215, 0, 0.15);
`;

const OwlGradeKey = styled.span`
  display: block;
  font-family: "Crimson Text", serif;
  font-size: 0.6rem;
  font-style: italic;
  color: #8b6914;
  letter-spacing: 0.05em;
  text-transform: none;
  margin-top: 0.3rem;
  text-shadow: none;
`;

const HighlightsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.4rem;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const HighlightItem = styled.li<{ $delay: number }>`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.45rem 0.6rem;
  border-radius: 5px;
  background: rgba(255, 215, 0, 0.02);
  border: 1px solid rgba(255, 215, 0, 0.04);
  transition: all 0.25s ease;

  &:hover {
    background: rgba(255, 215, 0, 0.06);
    border-color: rgba(255, 215, 0, 0.12);
    transform: translateX(3px);
  }
`;

const OwlGradeBadge = styled.span<{ $color: string }>`
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid ${({ $color }) => $color};
  background: ${({ $color }) => $color}15;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Cinzel", serif;
  font-size: 0.7rem;
  font-weight: 700;
  color: ${({ $color }) => $color};
  box-shadow: 0 0 8px ${({ $color }) => $color}25;
  text-shadow: 0 0 6px ${({ $color }) => $color}40;
`;

const OwlResultInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
`;

const OwlSubject = styled.span`
  font-family: "Cinzel", serif;
  font-size: 0.65rem;
  font-weight: 600;
  color: #e8dfd5;
  letter-spacing: 0.04em;
`;

const OwlDetail = styled.span`
  font-family: "Crimson Text", serif;
  font-size: 0.7rem;
  color: #8b7a60;
  line-height: 1.3;
`;

/* ── Perched Owl on letter ── */

const owlBreathe = keyframes`
  0%, 100% { transform: translateY(0) scaleY(1); }
  50% { transform: translateY(-2px) scaleY(1.02); }
`;

const owlBlink = keyframes`
  0%, 94%, 100% { transform: scaleY(1); }
  96% { transform: scaleY(0.1); }
`;

const hintFade = keyframes`
  0% { opacity: 0; transform: translateX(-50%) translateY(0); }
  15% { opacity: 1; transform: translateX(-50%) translateY(0); }
  85% { opacity: 1; transform: translateX(-50%) translateY(0); }
  100% { opacity: 0; transform: translateX(-50%) translateY(-4px); }
`;

const wingFlap = keyframes`
  0% { transform: rotate(0deg); }
  15% { transform: rotate(-30deg); }
  30% { transform: rotate(10deg); }
  45% { transform: rotate(-25deg); }
  60% { transform: rotate(8deg); }
  75% { transform: rotate(-20deg); }
  90% { transform: rotate(5deg); }
  100% { transform: rotate(0deg); }
`;

const wingFlapRight = keyframes`
  0% { transform: rotate(0deg); }
  15% { transform: rotate(30deg); }
  30% { transform: rotate(-10deg); }
  45% { transform: rotate(25deg); }
  60% { transform: rotate(-8deg); }
  75% { transform: rotate(20deg); }
  90% { transform: rotate(-5deg); }
  100% { transform: rotate(0deg); }
`;

const owlLift = keyframes`
  0%, 100% { transform: translateY(0); }
  20% { transform: translateY(-12px); }
  50% { transform: translateY(-8px); }
  80% { transform: translateY(-10px); }
`;

const PerchedOwlWrapper = styled.div<{ $flapping?: boolean }>`
  position: absolute;
  top: -80px;
  right: 10px;
  width: 100px;
  z-index: 3;
  animation: ${({ $flapping }) => $flapping ? owlLift : owlBreathe} ${({ $flapping }) => $flapping ? "2s" : "3s"} ease-in-out ${({ $flapping }) => $flapping ? "1" : "infinite"};
  filter: drop-shadow(0 6px 12px rgba(0,0,0,0.6));
  cursor: default;

  svg circle[fill="url(#owlEye)"] {
    animation: ${owlBlink} 4s ease-in-out infinite;
    transform-origin: center;
  }

  /* Wing flap animation */
  svg .owl-wing-left {
    transform-origin: 40px 45px;
    animation: ${wingFlap} 0.4s ease-in-out infinite;
  }

  svg .owl-wing-right {
    transform-origin: 80px 45px;
    animation: ${wingFlapRight} 0.4s ease-in-out infinite;
  }

  &:hover > span {
    animation: ${hintFade} 3s ease both;
  }

  @media (max-width: 768px) {
    width: 72px;
    top: -60px;
    right: 5px;
  }
`;

const OwlHint = styled.span`
  position: absolute;
  bottom: -18px;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  font-family: "Crimson Text", serif;
  font-size: 0.6rem;
  font-style: italic;
  color: #c4942a;
  opacity: 0;
  pointer-events: none;
  text-shadow: 0 0 8px rgba(0,0,0,0.6);

  @media (max-width: 768px) {
    font-size: 0.5rem;
    bottom: -14px;
  }
`;

/* ── Portkey Cards (card-style social links) ── */

const PortkeySection = styled.div`
  margin-bottom: 2.5rem;
`;

const PortkeyGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem;
  justify-content: center;
`;

const PortkeyCard = styled.a<{ $hoverColor: string }>`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.8rem 1.4rem;
  background: rgba(22, 18, 28, 0.9);
  border: 1px solid rgba(120, 100, 80, 0.15);
  border-radius: 14px;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  min-width: 140px;

  /* Dot indicator — hidden by default, appears on hover */
  &::after {
    content: "";
    position: absolute;
    bottom: -6px;
    left: 50%;
    transform: translateX(-50%) scale(0);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $hoverColor }) => $hoverColor};
    box-shadow: 0 0 10px ${({ $hoverColor }) => $hoverColor}80;
    transition: transform 0.3s ease;
  }

  &:hover {
    border-color: ${({ $hoverColor }) => $hoverColor}60;
    background: rgba(30, 25, 38, 0.95);
    transform: translateY(-4px);
    box-shadow:
      0 8px 25px rgba(0, 0, 0, 0.3),
      0 0 20px ${({ $hoverColor }) => $hoverColor}12,
      inset 0 0 20px ${({ $hoverColor }) => $hoverColor}05;

    &::after {
      transform: translateX(-50%) scale(1);
    }

    svg {
      fill: ${({ $hoverColor }) => $hoverColor};
      stroke: ${({ $hoverColor }) => $hoverColor};
      filter: drop-shadow(0 0 8px ${({ $hoverColor }) => $hoverColor}60);
    }

    span {
      color: #f0ece6;
    }
  }

  @media (max-width: 480px) {
    min-width: 110px;
    padding: 0.6rem 1rem;
    gap: 0.5rem;
  }
`;

const PortkeyCardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    transition: all 0.35s ease;
  }
`;

const PortkeyCardLabel = styled.span`
  font-family: "Raleway", sans-serif;
  font-size: 0.9rem;
  font-weight: 500;
  color: #a09888;
  transition: color 0.35s ease;
  white-space: nowrap;

  @media (max-width: 480px) {
    font-size: 0.78rem;
  }
`;

/* ── Broomstick + Accio Resume ── */

const broomFloat = keyframes`
  0%, 100% { transform: translateY(0px) rotate(-2deg); }
  50% { transform: translateY(-8px) rotate(0deg); }
`;

const speedDash = keyframes`
  0% { opacity: 0; transform: translateX(30px); }
  30% { opacity: 0.6; }
  100% { opacity: 0; transform: translateX(-80px); }
`;

const sparkleAnim = keyframes`
  0%, 100% { opacity: 0; transform: scale(0); }
  50% { opacity: 1; transform: scale(1); }
`;

/* ── Wizard References ── */
const RefsTitle = styled.h3`
  font-family: "Cinzel", serif;
  font-size: 0.7rem;
  font-weight: 700;
  color: #ffd700;
  letter-spacing: 0.35em;
  text-transform: uppercase;
  text-align: center;
  margin-bottom: 20px;
`;

const RefsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 3rem;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const RefCard = styled.div`
  position: relative;
  padding: 28px 24px;
  background: rgba(15, 12, 22, 0.7);
  border: 1px solid rgba(255, 200, 100, 0.08);
  border-radius: 14px;
  transition: border-color 0.3s;

  &:hover {
    border-color: rgba(255, 200, 100, 0.18);
  }
`;

const RefDecorTop = styled.div`
  width: 60px;
  height: 8px;
  margin: 0 auto 18px;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 8'%3E%3Cpath d='M0 4 Q10 0 15 4 Q20 8 30 4 Q40 0 45 4 Q50 8 60 4' fill='none' stroke='%238b6914' stroke-width='1' opacity='0.4'/%3E%3C/svg%3E") center no-repeat;
  background-size: contain;
`;

const RefDecorBottom = styled.div`
  width: 60px;
  height: 8px;
  margin: 18px auto 0;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 8'%3E%3Cpath d='M0 4 Q10 0 15 4 Q20 8 30 4 Q40 0 45 4 Q50 8 60 4' fill='none' stroke='%238b6914' stroke-width='1' opacity='0.4'/%3E%3C/svg%3E") center no-repeat;
  background-size: contain;
`;

const RefQuoteMark = styled.span`
  font-family: "Georgia", serif;
  font-size: 3rem;
  line-height: 1;
  color: #8b6914;
  opacity: 0.3;
  display: block;
  margin-bottom: -10px;
`;

const RefText = styled.p`
  font-family: "Crimson Text", serif;
  font-size: 0.95rem;
  font-style: italic;
  color: #a89a8a;
  line-height: 1.85;
  margin-bottom: 20px;
`;

const RefFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RefAvatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 2px solid rgba(139, 105, 20, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  background: rgba(139, 105, 20, 0.06);
  flex-shrink: 0;
`;

const RefInfo = styled.div`
  flex: 1;
`;

const RefName = styled.div`
  font-family: "Cinzel", serif;
  font-size: 0.82rem;
  font-weight: 700;
  color: #e8dfd5;
`;

const RefRole = styled.div`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.52rem;
  color: #7a6a5a;
  letter-spacing: 0.04em;
`;

const RefHouse = styled.span<{ $color: string }>`
  font-family: "Cinzel", serif;
  font-size: 0.55rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${({ $color }) => $color};
  padding: 4px 14px;
  border: 1px solid ${({ $color }) => $color}30;
  border-radius: 20px;
  white-space: nowrap;
`;

const BroomSection = styled.div`
  margin: 3rem 0 2.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
`;

const BroomContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 480px;
  height: 120px;
  animation: ${broomFloat} 4s ease-in-out infinite;
`;

const SpeedLines = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 25%;
  height: 100%;
  pointer-events: none;
`;

const SpeedLine = styled.div<{ $y: number; $delay: number; $width: number }>`
  position: absolute;
  top: ${({ $y }) => $y}%;
  left: 0;
  width: ${({ $width }) => $width}px;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 215, 0, 0.3), transparent);
  animation: ${speedDash} 1.5s ease-out ${({ $delay }) => $delay}s infinite;
`;

const BroomGlow = styled.div`
  position: absolute;
  left: 10%;
  top: 30%;
  width: 35%;
  height: 40%;
  background: radial-gradient(ellipse, rgba(255, 200, 50, 0.15), transparent 70%);
  filter: blur(15px);
  pointer-events: none;
`;

const BroomSVG = styled.svg`
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 4px 12px rgba(139, 105, 20, 0.3));
`;

const BroomSparkle = styled.div<{ $x: number; $y: number; $delay: number; $size: number }>`
  position: absolute;
  left: ${({ $x }) => $x}%;
  top: ${({ $y }) => $y}%;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: #ffd700;
  box-shadow: 0 0 6px rgba(255, 215, 0, 0.6);
  animation: ${sparkleAnim} ${({ $delay }) => 2 + $delay * 0.3}s ease-in-out ${({ $delay }) => $delay}s infinite;
`;

const accioShine = keyframes`
  0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
  100% { transform: translateX(200%) translateY(200%) rotate(45deg); }
`;

const AccioResumeBtn = styled.a`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.8rem;
  background: linear-gradient(135deg, #daa520, #c4942a, #b8860b);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  box-shadow: 0 4px 20px rgba(218, 165, 32, 0.25);
  min-width: 260px;
  position: relative;
  overflow: hidden;

  /* Diagonal shine sweep — top-left to bottom-right */
  &::before {
    content: "";
    position: absolute;
    top: -50%;
    left: -50%;
    width: 40%;
    height: 200%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.05) 25%,
      rgba(255, 255, 255, 0.25) 50%,
      rgba(255, 255, 255, 0.05) 75%,
      transparent 100%
    );
    transform: rotate(45deg);
    animation: ${accioShine} 3s ease-in-out infinite;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-3px) scale(1.02);
    box-shadow: 0 8px 30px rgba(218, 165, 32, 0.4), 0 0 40px rgba(255, 215, 0, 0.15);

    &::before {
      animation-duration: 1.5s;
    }
  }
`;

const AccioBtnIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.15);
  flex-shrink: 0;
`;

const AccioBtnText = styled.div`
  flex: 1;
  text-align: left;
`;

const AccioBtnTitle = styled.div`
  font-family: "Cinzel", serif;
  font-size: 1rem;
  font-weight: 700;
  font-style: italic;
  color: #1a0a00;
`;

const AccioBtnSub = styled.div`
  font-family: "Crimson Text", serif;
  font-size: 0.72rem;
  color: #3a2a1a;
  opacity: 0.8;
`;

const AccioBtnArrow = styled.span`
  font-size: 1.3rem;
  color: #1a0a00;
  flex-shrink: 0;
  transition: transform 0.3s ease;

  ${AccioResumeBtn}:hover & {
    transform: translateX(4px);
  }
`;

/* ── Letter CTA (bottom-right of letter) ── */
const LetterCTA = styled.a`
  position: absolute;
  bottom: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.8rem;
  background: linear-gradient(135deg, #8b0000, #5a0000);
  border-radius: 4px;
  color: #f5e6c8;
  font-family: "Cinzel", serif;
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(139, 0, 0, 0.4);
  z-index: 2;

  &:hover {
    background: linear-gradient(135deg, #a00000, #6a0000);
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(139, 0, 0, 0.6), 0 0 20px rgba(255, 215, 0, 0.1);
    color: #ffd700;
  }

  @media (max-width: 480px) {
    font-size: 0.58rem;
    padding: 0.35rem 0.6rem;
    bottom: 12px;
    right: 12px;
  }
`;

const LetterCTAIcon = styled.span`
  display: flex;
  align-items: center;
  opacity: 0.8;
`;

/* ── Footer glow ── */
const footerPulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.7; }
`;

const FooterGlow = styled.div`
  width: 100%;
  height: 2px;
  margin: 2rem 0 0.5rem;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 215, 0, 0.15) 15%,
    rgba(255, 215, 0, 0.4) 30%,
    rgba(218, 165, 32, 0.6) 50%,
    rgba(255, 215, 0, 0.4) 70%,
    rgba(255, 215, 0, 0.15) 85%,
    transparent 100%
  );
  border-radius: 1px;
  position: relative;
  animation: ${footerPulse} 3s ease-in-out infinite;

  &::before {
    content: "";
    position: absolute;
    top: -6px;
    left: 50%;
    transform: translateX(-50%);
    width: 60%;
    height: 14px;
    background: radial-gradient(ellipse at center, rgba(255, 215, 0, 0.08), transparent 70%);
    filter: blur(4px);
  }

  &::after {
    content: "⚡";
    position: absolute;
    top: -8px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 0.7rem;
    color: #ffd700;
    text-shadow: 0 0 8px rgba(255, 215, 0, 0.5);
  }
`;

const HogwartsQuote = styled.p`
  font-family: "Crimson Text", serif;
  font-size: 0.95rem;
  font-style: italic;
  color: #c4942a;
  padding: 1.2rem;
  border-top: none;
  line-height: 1.7;
  text-shadow: 0 0 20px rgba(255, 215, 0, 0.08);
  background: radial-gradient(ellipse at center, rgba(255, 215, 0, 0.03), transparent 70%);
  border-radius: 8px;
`;

const QuoteAuthor = styled.span`
  display: block;
  font-size: 0.75rem;
  margin-top: 0.5rem;
  color: #daa520;
  font-style: normal;
  letter-spacing: 0.12em;
  text-shadow: 0 0 10px rgba(218, 165, 32, 0.2);
`;
